import { flip } from '@floating-ui/react';
import { classNames, twMerge } from '@panwds/tailwindcss';
import React, { cloneElement, useEffect, useRef, useState } from 'react';
import { mergeRefs } from '../../utils/index.js';
import { getAutoPlacement, usePopper } from '../../utils/usePopper.js';
import { useShouldRender } from '../../utils/useShouldRender.js';
import { Button } from '../Button/index.js';
import { TruncateText } from '../TruncateText/index.js';
import { Body } from '../Typography/index.js';
import { Dropdown } from './Dropdown.js';
import { isDomElement, selectedItemLabel } from './utils.js';
/**
 * This component is like `Select` but always has searching enabled.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { SelectWithSearch } from "@panwds/react-ui";
 * ```
 */
export function SelectWithSearch({ 'data-autofocus': dataAutofocus, button, buttonRef, fullWidth = false, selectedItem, dataMetrics, dataTestId, enableInitialFocus = false, disabled, defaultPlacement = 'bottom-start', darkMode, placeholder, customWidth, fitToItemWidth = false, size = 'md', listItemSize, itemsShown = 10, allowDraftSelections = false, readOnly, footer, ...rest }) {
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const { placement, autoPlacement } = getAutoPlacement(defaultPlacement);
    const { refs, floatingStyles, getFloatingProps } = usePopper({
        placement,
        strategy: 'fixed',
        open: isOpen,
        onOpenChange: setIsOpen,
        ...(fitToItemWidth && {
            middleware: [
                autoPlacement,
                {
                    name: 'sameWidth',
                    options: {
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        enabled: true
                    },
                    fn: (state) => {
                        // todo: potential bug
                        state.elements.floating.style.width = `${state.rects.reference.width}px`;
                        return {};
                    }
                },
                flip()
            ]
        })
    });
    const shouldRender = useShouldRender(isOpen);
    const DefaultButton = (React.createElement(Button, { "data-autofocus": dataAutofocus, "data-testid": `${dataTestId ?? 'select'}-trigger`, fullWidth: fullWidth, isMenu: true, size: size, appearance: "tertiary", className: {
            button: twMerge(
            // TODO: Select min width should be `twds-min-w-34` as per figma, but due to major change, avoiding this for now.
            'twds-text-left twds-bg-white dark:twds-bg-black twds-bg-opacity-80 dark:twds-bg-opacity-16 twds-group', 'dark:twds-border-gray-500/30', 'hover:twds-bg-gray-200 dark:hover:twds-bg-gray-800', isOpen && 'twds-bg-gray-300 dark:twds-bg-gray-700', disabled && 'twds-opacity-1 dark:twds-opacity-40', readOnly &&
                'twds-bg-gray-100 dark:twds-bg-gray-800 dark:twds-bg-opacity-20 twds-cursor-not-allowed twds-opacity-100', rest.addButtonClassName),
            menuIconWrapper: classNames(disabled && 'twds-opacity-1 twds-text-gray-400', readOnly && 'twds-text-gray-400 dark:twds-text-gray-700')
        }, isOpen: isOpen },
        React.createElement(TruncateText, { addClassName: classNames('twds-flex-auto twds-text-left twds-font-normal', selectedItemLabel(selectedItem) ? 'twds-text-gray-900 dark:twds-text-gray-100' : 'twds-text-gray-500', disabled && 'twds-text-disabled', readOnly && 'twds-text-primary') }, selectedItemLabel(selectedItem) ||
            (placeholder ? (React.createElement(Body, { appearance: "secondary", addClassName: classNames('twds-text-xs twds-text-gray-500', !disabled &&
                    !isOpen &&
                    'dark:group-hover:twds-text-gray-300 group-hover:twds-text-gray-800 dark:group-active:twds-text-gray-200', disabled && 'twds-text-disabled', isOpen && 'twds-text-gray-900 dark:twds-text-gray-200') }, placeholder)) : ('')))));
    const addProps = isDomElement(button?.type.toString()) ? {} : { isOpen };
    const trigger = cloneElement(button || DefaultButton, {
        ...addProps,
        id: rest.toggleButtonId,
        'aria-labelledby': `${rest.labelId ?? ''} ${rest.toggleButtonId ?? ''}`,
        'aria-expanded': isOpen,
        'aria-haspopup': 'listbox',
        ref: mergeRefs([
            buttonRef,
            anchorRef,
            (el) => !refs?.reference?.current && refs.setReference(el)
        ]),
        onMouseUp: () => setIsOpen(!isOpen),
        onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                setIsOpen(true);
            }
        },
        ...(customWidth && { style: { width: customWidth } }),
        disabled: disabled || readOnly
    });
    useEffect(() => {
        if (enableInitialFocus) {
            setTimeout(() => setIsOpen(true));
        }
    }, []);
    return (React.createElement("div", { className: classNames(darkMode && 'dark'), ...(customWidth && { style: { width: customWidth } }) },
        trigger,
        shouldRender && (React.createElement(Dropdown, { ...rest, dataMetrics: dataMetrics, dataTestId: dataTestId, darkMode: darkMode, isOpen: isOpen, selectedItem: selectedItem, anchorRef: anchorRef, containerRef: refs.setFloating, onOpenChange: setIsOpen, fullWidth: fullWidth, fitToItemWidth: fullWidth ? false : fitToItemWidth, listItemSize: listItemSize ?? size, itemsShown: itemsShown, allowDraftSelections: allowDraftSelections, footer: footer, floatingStyle: floatingStyles, getFloatingProp: getFloatingProps }))));
}
//# sourceMappingURL=SelectWithSearch.js.map